import type { Board, Tag, TagsResponse, Territory } from './types';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly errorCode: string,
    message: string,
  ) {
    super(message);
  }
}

/**
 * Told when a request comes back unauthenticated.
 *
 * A session can end while the console is open -- it expires, or somebody signs
 * out in another tab -- and it can end during any request, not only the one on
 * load. Rather than every caller checking for a 401, the client announces it
 * once and the shell puts the sign-in screen back up.
 */
type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler | null = null;

export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  onUnauthorized = handler;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  /*
   * Only declare a JSON content type when there is actually a body. Fastify
   * rejects a request that announces JSON and then sends nothing, so a bodyless
   * POST -- reconcile, for instance -- fails before it reaches a handler.
   */
  const headers: Record<string, string> = {
    ...((init.headers as Record<string, string>) ?? {}),
  };
  if (init.body !== undefined && init.body !== null) {
    headers['Content-Type'] = 'application/json';
  }

  /*
   * The session cookie identifies the console. It used to send a shared API key
   * compiled into the bundle, which meant every visitor to the page held a
   * credential for the whole account -- the console is a signed-in application
   * now, and keys are for integrations.
   */
  const res = await fetch(path, { ...init, headers, credentials: 'include' });

  if (!res.ok) {
    let code = 'REQUEST_FAILED';
    let message = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      code = body.errorCode ?? code;
      message = body.errorMessage ?? message;
    } catch {
      // A non-JSON error body is still an error; keep the status text.
    }
    /* Sign-in itself answers 401 for a bad password; that is not a lost session. */
    if (res.status === 401 && !path.startsWith('/v1/auth/')) onUnauthorized?.();
    throw new ApiError(res.status, code, message);
  }

  return res.status === 204 ? (null as T) : ((await res.json()) as T);
}

export type PricingModel = 'flat' | 'per_vehicle' | 'custom';

export interface PlanRow {
  code: string;
  name: string;
  tagline: string | null;
  description: string | null;
  pricing_model: PricingModel;
  price_cents: number;
  per_vehicle_cents: number;
  min_vehicles: number;
  currency: string;
  interval: 'month' | 'year';
  limits: Record<string, number | null>;
  features: Record<string, unknown>;
  feature_list: string[];
  highlight: boolean;
}

/** What a period costs, by the same formula the API charges with. */
export function planAmountCents(
  plan: Pick<PlanRow, 'pricing_model' | 'price_cents' | 'per_vehicle_cents' | 'min_vehicles'>,
  vehicles: number | null,
): number {
  if (plan.pricing_model !== 'per_vehicle') return plan.price_cents;
  const qty = Math.max(plan.min_vehicles, vehicles ?? plan.min_vehicles);
  return plan.price_cents + plan.per_vehicle_cents * qty;
}

export function money(cents: number | null | undefined, digits = 2): string {
  return ((cents ?? 0) / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

export type BillingStatus =
  | 'trialing'
  | 'trial_expired'
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'incomplete'
  | 'incomplete_expired'
  | 'unpaid'
  | 'paused';

export interface PaymentConfig {
  enabled: boolean;
  environment: 'sandbox' | 'production';
  apiLoginId: string | null;
  clientKey: string | null;
  acceptJsUrl: string;
}

export interface BillingSummary {
  plan: { code: string; name: string; pricingModel: PricingModel; features: Record<string, unknown> };
  status: BillingStatus;
  internal: boolean;
  trialEndsAt: string | null;
  trialDaysLeft: number | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  canceledAt: string | null;
  vehicles: number | null;
  amountCents: number | null;
  billingScheduled: boolean;
  card: { brand: string | null; last4: string | null } | null;
  lastPaymentError: string | null;
  usage: Record<string, { used: number; limit: number | null }>;
  payment: PaymentConfig;
}

export interface PaymentRow {
  id: string;
  transaction_id: string | null;
  amount_cents: number;
  currency: string;
  status: 'approved' | 'declined' | 'error' | 'held' | 'refunded' | 'voided';
  description: string | null;
  card_brand: string | null;
  card_last4: string | null;
  period_start: string | null;
  period_end: string | null;
  occurred_at: string;
}

export interface OpaqueData {
  dataDescriptor: string;
  dataValue: string;
}

export interface BillTo {
  firstName: string;
  lastName: string;
  company?: string;
  address?: string;
  city?: string;
  state?: string;
  zip: string;
  country?: string;
}

export interface SettingRow {
  key: string;
  label: string;
  help: string | null;
  type: 'string' | 'number' | 'boolean' | 'json';
  secret: boolean;
  env: string | null;
  /** A fixed set of values, where the setting has one. */
  options: { value: string; label: string }[] | null;
  /** The section it belongs under on the settings screen. */
  group: string | null;
  set: boolean;
  value?: unknown;
}

export interface ActivityRow {
  id: number;
  at: string;
  account_id?: string | null;
  account_name?: string | null;
  user_id?: string | null;
  actor: string | null;
  actor_kind: 'user' | 'api_key' | 'admin' | 'system' | 'anonymous';
  action: string;
  summary: string;
  target_type?: string | null;
  target_id?: string | null;
  ip: string | null;
  user_agent?: string | null;
}

export interface CallbackConfig {
  url: string;
  /** The password itself is never returned, only whether one is set. */
  signatureConfigured: boolean;
}

export interface WebhookDelivery {
  id: string;
  event: string;
  request_id: string | null;
  status: string;
  attempts: number;
  last_status: number | null;
  last_error: string | null;
  created_at: string;
  delivered_at: string | null;
}

export interface RecurrenceRow {
  id: string;
  orderTemplate: Record<string, unknown>;
  recurrencePeriod: {
    recurrenceType: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';
    period: number;
    weekday: number;
    cardinal: number;
    month: number;
  };
  recurrenceEndType: 'NEVER' | 'ON_DATE' | 'AFTER_N';
  numOfRecurrences: number;
  startDate: string | null;
  endDate: string | null;
}

export interface PlanSide {
  routes: number;
  stops: number;
  unassigned: number;
  distanceMeters: number;
  driveSeconds: number;
  latestEnd: number;
  windowsAtRisk: number;
}

export interface PlanComparison {
  date: string;
  name: string | null;
  simulation: PlanSide;
  operations: PlanSide;
  moved: { orderId: string; orderName: string; from: string; to: string }[];
  movedCount: number;
  droppedCount: number;
  resequencedCount: number;
}

export interface ApiKeyRow {
  id: string;
  label: string;
  token_hint: string;
  territory_id: string | null;
  access: 'full' | 'read';
  active: boolean;
  /** Active and not past its expiry: what the API will accept right now. */
  usable: boolean;
  expires_at: string | null;
  revoked_at: string | null;
  last_used_at: string | null;
  last_used_ip: string | null;
  created_at: string;
  created_by_email: string | null;
  revoked_by_email: string | null;
  calls_24h: number;
  calls_7d: number;
  errors_7d: number;
}

/** Only ever present on the response that creates it. */
export interface NewApiKey {
  id: string;
  label: string;
  token_hint: string;
  token: string;
}

export interface ApiKeyCall {
  id: number;
  at: string;
  method: string;
  path: string;
  status: number;
  duration_ms: number;
  error_code: string | null;
  ip: string | null;
}

export type Role = 'owner' | 'admin' | 'dispatcher' | 'viewer';

export interface UserRow {
  id: string;
  email: string;
  name: string | null;
  role: Role;
  status: 'active' | 'invited' | 'disabled';
  last_login_at: string | null;
  last_seen_at: string | null;
  created_at: string;
  invited_at: string | null;
  invited_by_email: string | null;
  invited_by_name: string | null;
  email_verified: boolean;
  has_password: boolean;
  has_google: boolean;
  sessions: number;
}

export interface LinkInfo {
  purpose: 'invite' | 'reset_password';
  email: string;
  name: string | null;
  accountName: string;
}

export interface Me {
  account: {
    id: string;
    name: string;
    slug: string;
    status: string;
    is_default: boolean;
    /* How this account reads distances and money, everywhere. */
    distance_unit: 'mi' | 'km';
    currency: string;
  };
  user: { id: string; email: string; role: string; emailVerified: boolean } | null;
  apiKey: { id: string } | null;
  territoryScope: string | null;
  billing: {
    status: BillingStatus;
    planName: string;
    trialEndsAt: string | null;
    trialDaysLeft: number | null;
    internal: boolean;
    billingScheduled: boolean;
  };
  marketingUrl: string;
  maps?: { attribution: string };
  /** True when an address becomes a position by itself (a geocoder is set up). */
  geocoding?: boolean;
  /** Set while a platform admin is signed in as this user from the admin panel. */
  /** The person's own saved preferences. */
  preferences?: { notifications?: { level: 'all' | 'important' | 'errors'; muted: string[] } };
  impersonation?: { adminEmail: string; adminName: string | null; reason: string | null; expiresAt: string } | null;
}

export const api = {
  providers: () =>
    request<{ password: boolean; google: boolean; signupOpen: boolean; signup: boolean }>(
      '/v1/auth/providers',
    ),

  me: () => request<Me>('/v1/auth/me'),

  login: (email: string, password: string) =>
    request<{ ok: true }>('/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  signup: (body: {
    accountName?: string;
    name: string;
    email: string;
    password: string;
    planCode?: string;
  }) =>
    request<{ id: string; email: string }>('/v1/auth/signup', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  logout: () => request<{ ok: true }>('/v1/auth/logout', { method: 'POST' }),

  resendVerification: () =>
    request<{ sent: boolean; alreadyVerified?: boolean }>('/v1/auth/verify-email/resend', { method: 'POST' }),

  updateFormats: (body: { distanceUnit?: 'mi' | 'km'; currency?: string }) =>
    request<{ id: string; distance_unit: 'mi' | 'km'; currency: string }>('/v1/account/formats', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  apiKeys: () => request<{ keys: ApiKeyRow[] }>('/v1/api-keys'),

  createApiKey: (body: { label: string; territoryId: string | null; access: 'full' | 'read'; expiresInDays: number | null }) =>
    request<NewApiKey>('/v1/api-keys', { method: 'POST', body: JSON.stringify(body) }),

  updateApiKey: (keyId: string, body: { label?: string; territoryId?: string | null; access?: 'full' | 'read' }) =>
    request<{ id: string }>(`/v1/api-keys/${keyId}`, { method: 'PUT', body: JSON.stringify(body) }),

  rollApiKey: (keyId: string, graceHours: number) =>
    request<NewApiKey>(`/v1/api-keys/${keyId}/roll`, { method: 'POST', body: JSON.stringify({ graceHours }) }),

  apiKeyCalls: (keyId: string) => request<{ calls: ApiKeyCall[] }>(`/v1/api-keys/${keyId}/calls`),

  revokeApiKey: (keyId: string) => request<null>(`/v1/api-keys/${keyId}`, { method: 'DELETE' }),

  users: () => request<{ users: UserRow[] }>('/v1/users'),

  /* With a password they are active immediately; without one they are emailed an invitation. */
  addUser: (body: { email: string; name?: string; role: string; password?: string }) =>
    request<{ id: string; email: string; status: string; invite: { sent: true } | { sent: false; reason: string } | null }>(
      '/v1/users',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  updateUser: (userId: string, body: { role?: string; status?: string; name?: string }) =>
    request<UserRow>(`/v1/users/${userId}`, { method: 'PUT', body: JSON.stringify(body) }),

  removeUser: (userId: string) => request<null>(`/v1/users/${userId}`, { method: 'DELETE' }),

  resendInvite: (userId: string) => request<{ sent: true }>(`/v1/users/${userId}/invite`, { method: 'POST' }),

  sendResetLink: (userId: string) => request<{ sent: true }>(`/v1/users/${userId}/reset-link`, { method: 'POST' }),

  signOutUser: (userId: string) => request<null>(`/v1/users/${userId}/sessions`, { method: 'DELETE' }),

  /* Returns the generated password once. There is nowhere to ask for it again. */
  setUserPassword: (userId: string) =>
    request<{ email: string; password: string }>(`/v1/users/${userId}/password`, {
      method: 'POST',
    }),

  /* ---- emailed links: invitations and resets ---- */

  inspectLink: (token: string) =>
    request<LinkInfo>('/v1/auth/link/inspect', { method: 'POST', body: JSON.stringify({ token }) }),

  completeLink: (body: { token: string; password: string; name?: string }) =>
    request<{ ok: true }>('/v1/auth/link/complete', { method: 'POST', body: JSON.stringify(body) }),

  savePreferences: (body: { notifications: { level: 'all' | 'important' | 'errors'; muted: string[] } }) =>
    request<{ preferences: Record<string, unknown> }>('/v1/auth/preferences', { method: 'PUT', body: JSON.stringify(body) }),

  forgotPassword: (email: string) =>
    request<{ received: true }>('/v1/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) }),

  changePassword: (currentPassword: string, newPassword: string) =>
    request<null>('/v1/auth/password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),

  /* ---- plan and billing ---- */

  /* Named for the price list, because `plans` already means route plans here. */
  pricingPlans: () => request<{ plans: PlanRow[] }>('/v1/plans'),

  billing: () => request<BillingSummary>('/v1/billing'),

  payments: () => request<{ payments: PaymentRow[] }>('/v1/billing/payments'),

  subscribe: (body: {
    planCode: string;
    vehicles?: number | null;
    opaqueData?: OpaqueData;
    billTo?: BillTo;
  }) =>
    request<{ status: string; chargedCents: number; amountCents: number; nextChargeOn: string }>(
      '/v1/billing/subscribe',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  setVehicles: (vehicles: number) =>
    request<{ vehicles: number; amountCents: number; proratedCents: number }>('/v1/billing/vehicles', {
      method: 'PUT',
      body: JSON.stringify({ vehicles }),
    }),

  replaceCard: (body: { opaqueData: OpaqueData; billTo: BillTo }) =>
    request<{ ok: true }>('/v1/billing/card', { method: 'PUT', body: JSON.stringify(body) }),

  cancelSubscription: (reason: string | null) =>
    request<{ accessUntil: string | null }>('/v1/billing/cancel', {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  /* ---- activity ---- */

  activity: (before?: number) =>
    request<{ activity: ActivityRow[]; next: number | null }>(
      `/v1/activity${before ? `?before=${before}` : ''}`,
    ),

  /* Fire and forget: a lost page view is not worth an error on screen. */
  reportView: (section: string) =>
    request<null>('/v1/activity/view', {
      method: 'POST',
      body: JSON.stringify({ section }),
    }).catch(() => null),




  territories: () => request<{ territories: Territory[] }>('/v1/territories'),

  board: (territoryId: string, date: string) =>
    request<Board>(`/v1/territories/${territoryId}/board?date=${date}`),

  /*
   * A plan's own board, drawn by the same map and timeline as the live day.
   * A second board component for simulations would drift from this one within
   * a release.
   */
  planBoard: (territoryId: string, planId: string) =>
    request<Board>(`/v1/territories/${territoryId}/board?planId=${planId}`),

  comparePlan: (territoryId: string, planId: string) =>
    request<PlanComparison>(`/v1/territories/${territoryId}/plans/${planId}/compare`),

  /** draft | planned | active | closed. `active` is what a driver app may see. */
  setPlanStatus: (territoryId: string, planId: string, status: string) =>
    request<Record<string, unknown>>(
      `/v1/territories/${territoryId}/plans/${planId}/status`,
      { method: 'POST', body: JSON.stringify({ status }) },
    ),

  /**
   * Empty a day. `dryRun` reports what would go without touching anything;
   * `force` is needed once any of it has been started.
   */
  clearDay: (
    territoryId: string,
    date: string,
    opts: { dryRun?: boolean; force?: boolean } = {},
  ) =>
    request<{
      date: string;
      deleted?: number;
      wouldDelete?: number;
      started: number;
      plansCleared?: number;
      dryRun?: boolean;
    }>(
      `/v1/territories/${territoryId}/orders?date=${encodeURIComponent(date)}` +
        (opts.dryRun ? '&dryRun=true' : '') +
        (opts.force ? '&force=true' : ''),
      { method: 'DELETE' },
    ),

  /**
   * Move working hours for one day, for one vehicle or the whole fleet.
   *
   * Writes a calendar exception for that date; the vehicle's standing shift is
   * untouched. Re-planning is a separate call, so the caller chooses its scope.
   */
  setDayShift: (
    territoryId: string,
    date: string,
    body: { vehicleId?: string; shiftStart?: number; shiftEnd?: number },
  ) =>
    request<{ date: string; vehicles: number; cleared: number }>(
      `/v1/territories/${territoryId}/plans/${date}/shift`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),

  /**
   * Place an unassigned order on a technician's day.
   *
   * The counterpart to moving a stop: there is no stop yet, so this makes one.
   * Honoured even when the arithmetic says it will not fit -- the route is
   * retimed and the board flags what that costs.
   */
  assignOrder: (
    territoryId: string,
    date: string,
    orderId: string,
    /** A route id, or `v:<vehicleId>` for a lane with no route yet. */
    target: string,
    toSequence: number,
    /** Push to the driver. Omitted: the server decides (yes when the day is today). */
    notify?: boolean,
  ) =>
    request<{ assigned: string; route: unknown }>(
      `/v1/territories/${territoryId}/plans/${date}/assign`,
      {
        method: 'POST',
        body: JSON.stringify({
          orderId,
          toSequence,
          ...(target.startsWith('v:') ? { toVehicleId: target.slice(2) } : { toRouteId: target }),
          ...(notify === undefined ? {} : { notify }),
        }),
      },
    ),

  /**
   * Take an order off the day's routes, leaving the order itself alone.
   *
   * It returns to the unassigned list, where the next build or a drag can place
   * it again. Not the same as deleting it.
   */
  unassignOrder: (territoryId: string, date: string, orderId: string) =>
    request<{ unassigned: string; route: unknown }>(
      `/v1/territories/${territoryId}/plans/${date}/unassign`,
      { method: 'POST', body: JSON.stringify({ orderId }) },
    ),

  /**
   * The road each route actually drives, for drawing.
   *
   * Asked for only when the dispatcher switches road shapes on, and answered
   * with `available: false` where there is no routing engine to ask -- the
   * control then says why rather than drawing the straight lines again under a
   * different name.
   */
  routePaths: (territoryId: string, date: string) =>
    request<{
      date: string;
      provider: string;
      available: boolean;
      reason?: string;
      paths: Record<string, [number, number][]>;
    }>(`/v1/territories/${territoryId}/plans/${date}/paths`),

  /** Delete an order outright. The day it was on is reconciled afterwards. */
  deleteOrder: (territoryId: string, orderId: string) =>
    request<null>(`/v1/territories/${territoryId}/orders/${orderId}`, { method: 'DELETE' }),

  /**
   * Lock or unlock every route on a day.
   *
   * A replan skips locked routes, so this is also how a dispatcher protects a
   * board they are happy with before re-planning one driver.
   */
  setDayLock: (territoryId: string, date: string, locked: boolean) =>
    request<{ date: string; locked: boolean; changed: number }>(
      `/v1/territories/${territoryId}/plans/${date}/lock`,
      { method: 'PUT', body: JSON.stringify({ locked }) },
    ),

  optimize: (
    territoryId: string,
    date: string,
    options: {
      timeLimit?: number;
      kind?: 'operations' | 'simulation';
      name?: string;
      /** Pin stops a dispatcher placed by hand. Defaults to true on the server. */
      keepManual?: boolean;
      /**
       * Re-plan only these vehicles, leaving every other route as it stands.
       * Empty, the default, is the whole fleet.
       */
      vehicleIds?: string[];
    } = {},
  ) =>
    request<{ requestId: string; planId: string }>(
      `/v1/territories/${territoryId}/plans/${date}/optimize`,
      { method: 'POST', body: JSON.stringify({ timeLimit: 20, ...options }) },
    ),

  plans: (territoryId: string) =>
    request<{ plans: Record<string, unknown>[] }>(`/v1/territories/${territoryId}/plans`),

  promotePlan: (territoryId: string, planId: string, force = false) =>
    request<{
      planId: string;
      routes: number;
      stops: number;
      skippedLocked: string[];
      droppedExecuted: string[];
    }>(`/v1/territories/${territoryId}/plans/${planId}/promote`, {
      method: 'POST',
      body: JSON.stringify({ force }),
    }),

  deletePlan: (territoryId: string, planId: string) =>
    request<null>(`/v1/territories/${territoryId}/plans/${planId}`, { method: 'DELETE' }),

  /**
   * `target` is a route id, or `v:<vehicleId>` for a vehicle with no route on
   * this plan yet -- the board offers idle vehicles as lanes, and the route is
   * created by the move that needs it.
   */
  /**
   * Hold a stop until a time, or release it with null.
   *
   * Sent through the move endpoint because it is the same edit: the stop stays
   * where it is and only its earliest arrival changes.
   */
  holdStop: (territoryId: string, stopId: string, notBeforeSec: number | null, toSequence: number) =>
    request<unknown>(`/v1/territories/${territoryId}/stops/${stopId}/move`, {
      method: 'POST',
      body: JSON.stringify({ toSequence, notBeforeSec }),
    }),

  moveStop: (territoryId: string, stopId: string, target: string, toSequence: number) =>
    request<unknown>(`/v1/territories/${territoryId}/stops/${stopId}/move`, {
      method: 'POST',
      body: JSON.stringify(
        target.startsWith('v:')
          ? { toVehicleId: target.slice(2), toSequence }
          : { toRouteId: target, toSequence },
      ),
    }),

  recompute: (territoryId: string, planId: string) =>
    request<unknown>(`/v1/territories/${territoryId}/plans/${planId}/recompute`, {
      method: 'POST',
    }),

  vehicles: (territoryId: string) =>
    request<{ vehicles: unknown[] }>(`/v1/territories/${territoryId}/vehicles`),

  depots: (territoryId: string) =>
    request<{ depots: unknown[] }>(`/v1/territories/${territoryId}/depots`),

  /** Push a message to drivers' phones: some of them, or every active one. */
  sendDriverNotification: (
    territoryId: string,
    body: { all?: boolean; technicianIds?: string[]; title: string; body: string },
  ) =>
    request<{ batchId: string; sent: number; results: DriverSendResult[] }>(
      `/v1/territories/${territoryId}/driver-notifications`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  driverNotifications: (territoryId: string, technicianId?: string, limit = 50) =>
    request<{ configured: boolean; notifications: DriverNotificationRow[] }>(
      `/v1/territories/${territoryId}/driver-notifications?limit=${limit}${technicianId ? `&technicianId=${technicianId}` : ''}`,
    ),

  technicians: (territoryId: string) =>
    request<{ technicians: unknown[] }>(`/v1/territories/${territoryId}/technicians`),

  tags: (territoryId: string) =>
    request<TagsResponse>(`/v1/territories/${territoryId}/tags`),

  createTag: (territoryId: string, body: { name: string; color?: string | null }) =>
    request<Tag>(`/v1/territories/${territoryId}/tags`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateTag: (
    territoryId: string,
    tagId: string,
    body: { label?: string; archived?: boolean },
  ) =>
    request<Tag>(`/v1/territories/${territoryId}/tags/${tagId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  mergeTag: (territoryId: string, tagId: string, intoTagId: string) =>
    request<{ merged: string; into: string; technicians: number; vehicles: number; orders: number }>(
      `/v1/territories/${territoryId}/tags/${tagId}/merge`,
      { method: 'POST', body: JSON.stringify({ intoTagId }) },
    ),

  deleteTag: (territoryId: string, tagId: string) =>
    request<{ archived?: boolean; slug?: string; usages?: number } | null>(
      `/v1/territories/${territoryId}/tags/${tagId}`,
      { method: 'DELETE' },
    ),

  reconcileTags: (territoryId: string) =>
    request<{ registered: string[]; count: number }>(
      `/v1/territories/${territoryId}/tags/reconcile`,
      { method: 'POST' },
    ),

  lockRoute: (territoryId: string, routeId: string, locked: boolean) =>
    request<{ id: string; locked: boolean }>(
      `/v1/territories/${territoryId}/routes/${routeId}/lock`,
      { method: 'PUT', body: JSON.stringify({ locked }) },
    ),

  reverseRoute: (territoryId: string, routeId: string) =>
    request<{ reversed: number; stops: number }>(
      `/v1/territories/${territoryId}/routes/${routeId}/reverse`,
      { method: 'POST' },
    ),

  createTerritory: (body: Record<string, unknown>) =>
    request<Record<string, unknown>>('/v1/territories', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  deleteTerritory: (territoryId: string) =>
    request<null>(`/v1/territories/${territoryId}`, { method: 'DELETE' }),

  updateTerritory: (territoryId: string, body: Record<string, unknown>) =>
    request<Record<string, unknown>>(`/v1/territories/${territoryId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  createDepot: (territoryId: string, body: Record<string, unknown>) =>
    request<Record<string, unknown>>(`/v1/territories/${territoryId}/depots`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateDepot: (territoryId: string, depotId: string, body: Record<string, unknown>) =>
    request<Record<string, unknown>>(`/v1/territories/${territoryId}/depots/${depotId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  deleteDepot: (territoryId: string, depotId: string) =>
    request<null>(`/v1/territories/${territoryId}/depots/${depotId}`, { method: 'DELETE' }),

  updateVehicle: (territoryId: string, vehicleId: string, body: Record<string, unknown>) =>
    request<Record<string, unknown>>(`/v1/territories/${territoryId}/vehicles/${vehicleId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  /**
   * Orders, optionally narrowed.
   *
   * Omitting `date` searches every day, which is the point: a customer rings
   * quoting an identifier and has no idea when the job is booked.
   *
   * `q` is fuzzy and covers the order, its customer, its address and its custom
   * fields. `custom` is exact containment on one field, given as `key:value` --
   * what an integration wants when it is looking up its own record and must not
   * be given a near miss.
   */
  orders: (
    territoryId: string,
    params: { date?: string; q?: string; custom?: string; limit?: number } = {},
  ) => {
    const search = new URLSearchParams();
    if (params.date) search.set('date', params.date);
    if (params.q) search.set('q', params.q);
    if (params.custom) search.set('custom', params.custom);
    search.set('limit', String(params.limit ?? 500));
    return request<{ orders: Record<string, unknown>[] }>(
      `/v1/territories/${territoryId}/orders?${search.toString()}`,
    );
  },

  executionEvent: (territoryId: string, body: Record<string, unknown>) =>
    request<{ id: string; stopId: string | null }>(
      `/v1/territories/${territoryId}/execution-events`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /**
   * Where one more stop would fit on a day. A preview: nothing is written.
   * Confirming is `createOrder` then `assignOrder` with the option's slot.
   */
  fitIn: (territoryId: string, date: string, body: FitRequest) =>
    request<FitResponse>(`/v1/territories/${territoryId}/plans/${date}/fit`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** Address suggestions while typing. One `session` per typing run. */
  placesAutocomplete: (territoryId: string, q: string, session: string) =>
    request<{ available: boolean; error?: string; suggestions: PlaceSuggestion[] }>(
      `/v1/territories/${territoryId}/places/autocomplete?q=${encodeURIComponent(q)}&session=${encodeURIComponent(session)}`,
    ),

  /** A picked suggestion's position and address; ends the typing session. */
  placesResolve: (territoryId: string, placeId: string, session: string) =>
    request<{ location: { lat: number; lng: number }; formatted: string; address: PlaceAddress }>(
      `/v1/territories/${territoryId}/places/resolve`,
      { method: 'POST', body: JSON.stringify({ placeId, session }) },
    ),

  createOrder: (territoryId: string, body: Record<string, unknown>) =>
    request<{ orders: Record<string, unknown>[] }>(`/v1/territories/${territoryId}/orders`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  order: (territoryId: string, orderId: string) =>
    request<Record<string, unknown>>(`/v1/territories/${territoryId}/orders/${orderId}`),

  updateOrder: (territoryId: string, orderId: string, body: Record<string, unknown>) =>
    request<Record<string, unknown>>(`/v1/territories/${territoryId}/orders/${orderId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  createTechnician: (territoryId: string, body: Record<string, unknown>) =>
    request<Record<string, unknown>>(`/v1/territories/${territoryId}/technicians`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /* A driver's app password: give one, or none to have one generated and returned once. */
  setDriverPassword: (territoryId: string, technicianId: string, password?: string) =>
    request<{ set: true; email: string; password?: string }>(
      `/v1/territories/${territoryId}/technicians/${technicianId}/password`,
      { method: 'POST', body: JSON.stringify(password ? { password } : {}) },
    ),

  /* Signs the driver out of the app on every device; the password stays. */
  signOutDriverApp: (territoryId: string, technicianId: string) =>
    request<null>(`/v1/territories/${territoryId}/technicians/${technicianId}/sessions`, { method: 'DELETE' }),

  clearDriverPassword: (territoryId: string, technicianId: string) =>
    request<null>(`/v1/territories/${territoryId}/technicians/${technicianId}/password`, { method: 'DELETE' }),

  updateTechnician: (territoryId: string, technicianId: string, body: Record<string, unknown>) =>
    request<Record<string, unknown>>(
      `/v1/territories/${territoryId}/technicians/${technicianId}`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),

  deleteTechnician: (territoryId: string, technicianId: string) =>
    request<null>(`/v1/territories/${territoryId}/technicians/${technicianId}`, {
      method: 'DELETE',
    }),

  createVehicle: (territoryId: string, body: Record<string, unknown>) =>
    request<Record<string, unknown>>(`/v1/territories/${territoryId}/vehicles`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  deleteVehicle: (territoryId: string, vehicleId: string) =>
    request<null>(`/v1/territories/${territoryId}/vehicles/${vehicleId}`, { method: 'DELETE' }),

  vehicleRegions: (territoryId: string, vehicleId: string) =>
    request<{ regions: Record<string, unknown>[] }>(
      `/v1/territories/${territoryId}/vehicles/${vehicleId}/regions`,
    ),

  saveVehicleRegions: (
    territoryId: string,
    vehicleId: string,
    regions: { regionId: string; mode: 'allow' | 'deny' }[],
  ) =>
    request<{ vehicleId: string }>(
      `/v1/territories/${territoryId}/vehicles/${vehicleId}/regions`,
      { method: 'PUT', body: JSON.stringify({ regions }) },
    ),

  vehicleExceptions: (territoryId: string, vehicleId: string) =>
    request<{ exceptions: Record<string, unknown>[] }>(
      `/v1/territories/${territoryId}/vehicles/${vehicleId}/exceptions`,
    ),

  createVehicleException: (
    territoryId: string,
    vehicleId: string,
    body: Record<string, unknown>,
  ) =>
    request<Record<string, unknown>>(
      `/v1/territories/${territoryId}/vehicles/${vehicleId}/exceptions`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  updateVehicleException: (
    territoryId: string,
    vehicleId: string,
    exceptionId: string,
    body: Record<string, unknown>,
  ) =>
    request<Record<string, unknown>>(
      `/v1/territories/${territoryId}/vehicles/${vehicleId}/exceptions/${exceptionId}`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),

  deleteVehicleException: (territoryId: string, vehicleId: string, exceptionId: string) =>
    request<null>(
      `/v1/territories/${territoryId}/vehicles/${vehicleId}/exceptions/${exceptionId}`,
      { method: 'DELETE' },
    ),

  callback: (territoryId: string) =>
    request<CallbackConfig>(`/v1/territories/${territoryId}/callback`),

  saveCallback: (
    territoryId: string,
    body: { url: string; signaturePassword?: string; test?: boolean },
  ) =>
    request<CallbackConfig>(`/v1/territories/${territoryId}/callback`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  deleteCallback: (territoryId: string) =>
    request<null>(`/v1/territories/${territoryId}/callback`, { method: 'DELETE' }),

  webhookDeliveries: (territoryId: string) =>
    request<{ deliveries: WebhookDelivery[] }>(
      `/v1/territories/${territoryId}/webhook-deliveries`,
    ),

  retryWebhookDelivery: (territoryId: string, deliveryId: string) =>
    request<{ id: string }>(
      `/v1/territories/${territoryId}/webhook-deliveries/${deliveryId}/retry`,
      { method: 'POST' },
    ),

  /*
   * Recurrences live on the compatibility surface and nowhere else. Adding a
   * second implementation under /v1 purely so the console could avoid the word
   * "compat" would mean two code paths writing the same rows, which is the one
   * thing this codebase is most careful not to do.
   */
  recurrences: (territoryId: string) =>
    request<{ recurrences: Record<string, RecurrenceRow> }>(
      `/api/v1/territories/${territoryId}/recurrences`,
    ),

  addRecurrence: (territoryId: string, body: Record<string, unknown>) =>
    request<{ recurrences: Record<string, RecurrenceRow> }>(
      `/api/v1/territories/${territoryId}/recurrences`,
      { method: 'POST', body: JSON.stringify({ addRecurrencesList: [body] }) },
    ),

  updateRecurrence: (territoryId: string, body: Record<string, unknown>) =>
    request<{ requestId: string }>(
      `/api/v1/territories/${territoryId}/recurrences/updaterecurrences`,
      { method: 'PUT', body: JSON.stringify({ recurrencesUpdate: [body] }) },
    ),

  endRecurrence: (territoryId: string, id: string, endDate: string | null) =>
    request<{ recurrenceIds: string[] }>(`/api/v1/territories/${territoryId}/recurrences`, {
      method: 'DELETE',
      body: JSON.stringify({ recurrences: [{ id, endDate }] }),
    }),

  generateRecurrentOrders: (territoryId: string, from?: string, to?: string) =>
    request<{ generated: number }>(
      `/api/v1/territories/${territoryId}/recurrences/orders`,
      { method: 'POST', body: JSON.stringify({ ...(from ? { from } : {}), ...(to ? { to } : {}) }) },
    ),

  communication: (territoryId: string) =>
    request<Record<string, unknown>>(`/v1/territories/${territoryId}/communication`),

  saveCommunication: (territoryId: string, body: Record<string, unknown>) =>
    request<Record<string, unknown>>(`/v1/territories/${territoryId}/communication`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  mobileApp: (territoryId: string) =>
    request<Record<string, unknown>>(`/v1/territories/${territoryId}/mobile-app`),

  saveMobileApp: (territoryId: string, body: Record<string, unknown>) =>
    request<Record<string, unknown>>(`/v1/territories/${territoryId}/mobile-app`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  regions: (territoryId: string) =>
    request<{ regions: Record<string, unknown>[] }>(`/v1/territories/${territoryId}/regions`),

  createRegion: (territoryId: string, body: Record<string, unknown>) =>
    request<Record<string, unknown>>(`/v1/territories/${territoryId}/regions`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  deleteRegion: (territoryId: string, regionId: string) =>
    request<null>(`/v1/territories/${territoryId}/regions/${regionId}`, { method: 'DELETE' }),

  traffic: (territoryId: string) =>
    request<{ entries: { weekday: number | null; hour: number; factor: number }[] }>(
      `/v1/territories/${territoryId}/traffic`,
    ),

  saveTraffic: (
    territoryId: string,
    entries: { weekday: number | null; hour: number; factor: number }[],
  ) =>
    request<{ entries: unknown[] }>(`/v1/territories/${territoryId}/traffic`, {
      method: 'PUT',
      body: JSON.stringify({ entries }),
    }),

  roadEdits: (territoryId: string) =>
    request<{ edits: Record<string, unknown>[] }>(`/v1/territories/${territoryId}/road-edits`),

  deleteRoadEdit: (territoryId: string, editId: string) =>
    request<null>(`/v1/territories/${territoryId}/road-edits/${editId}`, { method: 'DELETE' }),

  /* ---- audit trail ---- */

  auditCalls: (params: Record<string, string> = {}) =>
    request<{ calls: Record<string, unknown>[]; nextBefore: number | null }>(
      `/v1/audit/calls?${new URLSearchParams(params).toString()}`,
    ),

  auditCall: (callId: string) =>
    request<Record<string, unknown>>(`/v1/audit/calls/${callId}`),

  /* Records are written in batches; this forces the buffer out first. */
  flushAudit: () => request<{ ok: true }>('/v1/audit/flush', { method: 'POST' }),

  auditWebhooks: (params: Record<string, string> = {}) =>
    request<{ deliveries: Record<string, unknown>[] }>(
      `/v1/audit/webhooks?${new URLSearchParams(params).toString()}`,
    ),

  auditWebhook: (deliveryId: string) =>
    request<Record<string, unknown>>(`/v1/audit/webhooks/${deliveryId}`),

  replayWebhook: (deliveryId: string, body: { url?: string }) =>
    request<{ id: string; status: string; url: string; replays: number }>(
      `/v1/audit/webhooks/${deliveryId}/replay`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  driverAssignments: (territoryId: string) =>
    request<{ assignments: Record<string, unknown>[] }>(
      `/v1/territories/${territoryId}/driver-assignments`,
    ),

  driverAssignment: (territoryId: string, id: string) =>
    request<Record<string, unknown>>(
      `/v1/territories/${territoryId}/driver-assignments/${id}`,
    ),

  saveDriverAssignment: (territoryId: string, id: string, body: Record<string, unknown>) =>
    request<Record<string, unknown>>(
      `/v1/territories/${territoryId}/driver-assignments/${id}`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),

  createDriverAssignment: (territoryId: string, body: Record<string, unknown>) =>
    request<Record<string, unknown>>(`/v1/territories/${territoryId}/driver-assignments`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  deleteDriverAssignment: (territoryId: string, id: string) =>
    request<null>(`/v1/territories/${territoryId}/driver-assignments/${id}`, {
      method: 'DELETE',
    }),

  orderNotifications: (territoryId: string, orderId: string) =>
    request<{ notifications: Record<string, unknown>[] }>(
      `/v1/territories/${territoryId}/orders/${orderId}/notifications`,
    ),

  previewNotification: (territoryId: string, body: { subject: string; body: string }) =>
    request<{
      subject: string;
      body: string;
      unknown: string[];
      known: readonly string[];
      sampledFrom: string | null;
    }>(`/v1/territories/${territoryId}/notifications/preview`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  notifications: (territoryId: string) =>
    request<{ rules: Record<string, unknown>[] }>(`/v1/territories/${territoryId}/notifications`),

  saveNotification: (territoryId: string, body: Record<string, unknown>) =>
    request<Record<string, unknown>>(`/v1/territories/${territoryId}/notifications`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  /*
   * EventSource sends no headers, but it does send cookies on a same-origin
   * URL, which is how this endpoint is authenticated now. The key that used to
   * ride in the query string was written into every server log it passed.
   */
  streamUrl: (territoryId: string, date: string) =>
    `/v1/territories/${territoryId}/stream?date=${date}`,
};

export type FitRanking = 'driving' | 'earliest' | 'disruption' | 'lightest';

export interface FitRequest {
  address?: string;
  location?: { lat: number; lng: number };
  serviceTime: number;
  timeWindows: { start: number; end: number }[];
}

export interface FitStopRef {
  stopId: string;
  label: string | null;
  name: string;
  lat: number;
  lng: number;
}

export interface FitOption {
  position: number;
  arrival: number;
  departure: number;
  waitSeconds: number;
  addedDriveSeconds: number;
  addedDistanceMeters: number;
  addedWorkSeconds: number;
  routeEnd: number;
  maxPushSeconds: number;
  newlyLate: string[];
  inWindow: boolean;
  addedOvertimeSeconds: number;
  overStopLimit: boolean;
  verdict: 'fits' | 'risky' | 'late';
  /** This option's place under each ranking, across every route. */
  rank: Record<FitRanking, number>;
  toSequence: number;
  afterStop: FitStopRef | null;
  beforeStop: FitStopRef | null;
}

export interface FitLane {
  routeId: string | null;
  vehicleId: string;
  vehicleName: string;
  technicianName: string | null;
  routeNumber: number | null;
  vehicleColor: string | null;
  shiftStart: number;
  shiftEnd: number;
  skipped: string | null;
  live?: boolean;
  /** No progress reported today, so the plan was taken as what has happened. */
  assumed?: boolean;
  start?: { lat: number; lng: number; stop: FitStopRef | null };
  end?: { lat: number; lng: number } | null;
  baseRouteEnd?: number;
  remainingStops?: number;
  plannedStops?: number;
  options?: FitOption[];
}

export interface FitResponse {
  date: string;
  live: boolean;
  now: number | null;
  location: { lat: number; lng: number };
  distanceUnit: 'mi' | 'km';
  matrix: string;
  routes: FitLane[];
}

export interface PlaceSuggestion {
  placeId: string;
  text: string;
  main: string;
  secondary: string;
}

export interface PlaceAddress {
  line1: string | null;
  city: string | null;
  region: string | null;
  postcode: string | null;
  country: string | null;
}

export interface DriverSendResult {
  technicianId: string;
  name: string | null;
  status: 'sent' | 'failed' | 'no_device' | 'not_configured';
  devices: number;
  error: string | null;
}

export interface DriverNotificationRow {
  id: string;
  batch_id: string;
  technician_id: string | null;
  technician_name: string | null;
  kind: 'manual' | 'stop_added' | 'stop_removed';
  title: string;
  body: string;
  status: DriverSendResult['status'];
  devices: number;
  error: string | null;
  sent_by: string | null;
  created_at: string;
}
